import React, { Fragment } from 'react';
import { SxProp, styled, useDripsyTheme, useSx } from 'dripsy';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';

import { passIf } from '@utils';
import { IconTouchable } from '../../common/IconTouchable';
import { Separator } from '../../common/Separator';
import { Tooltip, TooltipPositionEnum } from '../Tooltip';
import { PlayerTimeText } from './PlayerTimeText';
import { VolumeSlider } from './VolumeSlider';
import { ISubtitlesMenuProps, SubtitlesMenu } from './SubtitlesMenu';
import { VolumeIconTouchable } from './VolumeIconTouchable';
import { selectPlayIcon } from './videoPlayerUtils';
import type { IVideoPlayerStore } from './VideoPlayer';
import { TimestampText, TimestampType } from './TimestampText';
import {
  IPlaybackRateMenuProps,
  PlaybackRateMenu,
} from './PlaybackRateMenu';
import { useVideoPlayerSettings } from './hooks/useVideoPlayerSettings';

const Container = styled(View)({
  pt: '$7',
  width: '100%',
  justifyContent: 'space-between',
  flexDirection: 'row',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const MainControlRowContainer = styled(RowContainer)({
  flex: 1,
});

const volumeSliderContainerStyle: SxProp = {
  flexDirection: 'row',
  alignItems: 'center',
  width: 0,
};

const VerticalLine = styled(View)({
  bg: '$background0',
  width: 1,
  height: '100%',
});

export interface IWebPlayerControlProps
  extends Pick<ISubtitlesMenuProps, 'onVisibleChange'>,
    Pick<IPlaybackRateMenuProps, 'onVisibleChange'> {
  store: IVideoPlayerStore;
  onTogglePlay(): void;
  onToggleFullScreen(): void;
  progress: Animated.SharedValue<number>;
  max: Animated.SharedValue<number>;
  volume: Animated.SharedValue<number>;
  onNextVideo(): void;
  onPreviousVideo(): void;
  hasNextVideoButton: boolean;
  hasPreviousVideoButton: boolean;
  timestamps: TimestampType;
  onPressTimestamp(): void;
  hasSubtitles?: boolean;
}

const TOOLTIP_OFFSET = 35;

export const WebPlayerControl = observer<IWebPlayerControlProps>(
  ({
    store: videoPlayerStore,
    onTogglePlay,
    onToggleFullScreen,
    progress,
    max,
    volume,
    hasNextVideoButton,
    hasPreviousVideoButton,
    onNextVideo,
    onPreviousVideo,
    timestamps,
    onPressTimestamp,
    hasSubtitles,
  }) => {
    const videoPlayerSettings = useVideoPlayerSettings();
    const { theme } = useDripsyTheme();
    const { t } = useTranslation();
    const sx = useSx();
    const store = useLocalObservable(() => ({
      isHovered: false,
      isSliding: false,

      onMouseEnter() {
        this.isHovered = true;
      },

      onMouseLeave() {
        this.isHovered = false;
      },

      onSlidingStart() {
        this.isSliding = true;
      },

      onSlidingComplete() {
        this.isSliding = false;
      },

      get isShowSlider() {
        return this.isHovered || this.isSliding;
      },
    }));
    const minVolume = useSharedValue(0);
    const maxVolume = useSharedValue(1);

    const aIsShowSlider = store.isShowSlider;
    const animatedVolumeSliderContainerStyle = useAnimatedStyle(
      () => ({
        width: withTiming(aIsShowSlider ? 100 + theme.space.$5 : 0, {
          duration: 200,
        }),
        opacity: withTiming(aIsShowSlider ? 1 : 0, {
          duration: 200,
        }),
      }),
    );

    // We need to handle maxWidth manually because AnimatedText is animated TextInput
    // under the hood and the width of the TextInput is not responsive to TextInput content
    // or TextInput extra space which causing UI issues
    const playerTimeTextContainerStyle = useAnimatedStyle(() => {
      const secondInMs = 1000;
      const minuteInMs = secondInMs * 60;
      const hourInMs = minuteInMs * 60;

      if (max.value >= hourInMs) {
        return {
          maxWidth: 130,
        };
      } else {
        return {
          maxWidth: 90,
        };
      }
    });

    const playButtonIconName = selectPlayIcon({
      isPlaying: videoPlayerStore.isPlaying,
      isFinished: videoPlayerStore.isFinished,
    });

    const fullScreenButtonIconName = videoPlayerStore.isInFullScreen
      ? 'small-screen-bold'
      : 'full-screen';

    const playAndStopTooltipPosition = passIf(
      !hasPreviousVideoButton,
      TooltipPositionEnum.TopLeft,
    );

    const hasTimestamps = timestamps.length > 0;

    return (
      <Container>
        <MainControlRowContainer
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onMouseLeave={store.onMouseLeave}
        >
          {hasPreviousVideoButton && (
            <Fragment>
              <Tooltip
                text={t('common.videoPlayer.previousVideo')}
                offset={TOOLTIP_OFFSET}
                position={TooltipPositionEnum.TopLeft}
              >
                <IconTouchable
                  name="previous-bold"
                  color="$background0"
                  onPress={onPreviousVideo}
                />
              </Tooltip>
              <Separator size="$9" vertical />
            </Fragment>
          )}

          <Tooltip
            text={t('common.videoPlayer.playAndStop')}
            offset={TOOLTIP_OFFSET}
            position={playAndStopTooltipPosition}
          >
            <IconTouchable
              name={playButtonIconName}
              color="$background0"
              onPress={onTogglePlay}
            />
          </Tooltip>

          {hasNextVideoButton && (
            <Fragment>
              <Separator size="$9" vertical />
              <Tooltip
                text={t('common.videoPlayer.nextVideo')}
                offset={TOOLTIP_OFFSET}
              >
                <IconTouchable
                  name="next-bold"
                  color="$background0"
                  onPress={onNextVideo}
                />
              </Tooltip>
            </Fragment>
          )}
          <Separator size="$8" vertical />
          <RowContainer
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onMouseEnter={store.onMouseEnter}
          >
            <Tooltip
              text={t('common.videoPlayer.volume')}
              offset={TOOLTIP_OFFSET}
            >
              <VolumeIconTouchable
                volume={volume}
                onToggleVolume={videoPlayerSettings.setVolume}
              />
            </Tooltip>
            <Animated.View
              style={[
                sx(volumeSliderContainerStyle),
                animatedVolumeSliderContainerStyle,
              ]}
            >
              <Separator size="$5" vertical />
              <View>
                <VolumeSlider
                  progress={volume}
                  maximumValue={maxVolume}
                  minimumValue={minVolume}
                  onValueChange={videoPlayerSettings.setVolume}
                  // FIXME: onSlidingStart is not working
                  onSlidingStart={store.onSlidingStart}
                  onSlidingComplete={store.onSlidingComplete}
                />
              </View>
            </Animated.View>
          </RowContainer>
          <Separator size="$8" vertical />

          <Animated.View style={playerTimeTextContainerStyle}>
            <PlayerTimeText progress={progress} maximumValue={max} />
          </Animated.View>

          {hasTimestamps && (
            <Fragment>
              <Separator vertical />
              <VerticalLine />
              <Separator vertical />
              <TimestampText
                progress={progress}
                timestamps={timestamps}
                onPress={onPressTimestamp}
              />
            </Fragment>
          )}
        </MainControlRowContainer>
        <RowContainer>
          {hasSubtitles && (
            <Tooltip
              text={t('common.videoPlayer.subtitles')}
              offset={TOOLTIP_OFFSET}
            >
              <SubtitlesMenu
                onVisibleChange={
                  videoPlayerStore.setIsVisibleSubtitlesMenu
                }
              />
            </Tooltip>
          )}

          <Separator size="$7" vertical />

          <Tooltip
            text={t('common.videoPlayer.playbackRate')}
            offset={TOOLTIP_OFFSET}
          >
            <PlaybackRateMenu
              onVisibleChange={
                videoPlayerStore.setIsVisiblePlaybackRateMenu
              }
            />
          </Tooltip>

          <Separator size="$7" vertical />

          <Tooltip
            text={t('tutorial.fullScreenMode')}
            offset={TOOLTIP_OFFSET}
            position={TooltipPositionEnum.TopRight}
          >
            <IconTouchable
              name={fullScreenButtonIconName}
              color="$background0"
              onPress={onToggleFullScreen}
            />
          </Tooltip>
        </RowContainer>
      </Container>
    );
  },
);
